import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import * as stripeService from '../services/stripeService';
import * as pricingPlanService from '../services/pricingPlanService';
import type { PricingPlan } from '../services/pricingPlanService';

export default function StripePayments(): React.ReactElement {
  const { token } = useAuth();
  const [config, setConfig] = useState({
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    stripe_mode: 'test' as 'test' | 'live',
  });
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasSecretKey, setHasSecretKey] = useState(false);
  const [hasWebhookSecret, setHasWebhookSecret] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);
  const [plansSynced, setPlansSynced] = useState(false);
  const [billingPlans, setBillingPlans] = useState<PricingPlan[]>([]);

  useEffect(() => {
    async function loadConfig() {
      if (!token) return;
      try {
        const [data, plans] = await Promise.all([
          stripeService.getStripeConfiguration(token),
          pricingPlanService.getAllPlans(token),
        ]);
        if (data) {
          setConfig(prev => ({
            ...prev,
            stripe_publishable_key: data.stripe_publishable_key || '',
            stripe_mode: data.stripe_mode,
          }));
          setHasSecretKey(data.has_secret_key);
          setHasWebhookSecret(data.has_webhook_secret);
        }
        setBillingPlans(plans.filter(p => p.is_active && p.role_key && (p.price_monthly || 0) > 0));
      } catch (error) {
        console.error('Error loading Stripe config:', error);
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, [token]);

  async function handleSave() {
    if (!token) return;
    setSaving(true);
    setSaveError('');
    try {
      const payload: any = {
        stripe_publishable_key: config.stripe_publishable_key,
        stripe_mode: config.stripe_mode,
      };
      if (config.stripe_secret_key) {
        payload.stripe_secret_key = config.stripe_secret_key;
      }
      if (config.stripe_webhook_secret) {
        payload.stripe_webhook_secret = config.stripe_webhook_secret;
      }
      const data = await stripeService.saveStripeConfiguration(payload, token);
      setHasSecretKey(data.has_secret_key);
      setHasWebhookSecret(data.has_webhook_secret);
      // Clear the secret fields after save (they're now stored encrypted)
      setConfig(prev => ({ ...prev, stripe_secret_key: '', stripe_webhook_secret: '' }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error: any) {
      console.error('Error saving Stripe config:', error);
      setSaveError(error.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  }

  async function handleTestConnection() {
    if (!token) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await stripeService.testStripeConnection(token);
      setTestResult({
        success: true,
        message: `Connected (${result.mode} mode)`,
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || 'Connection failed',
      });
    } finally {
      setTesting(false);
    }
  }

  async function handleSyncPlans() {
    if (!token) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await stripeService.syncPlansToStripe(token);
      setPlansSynced(true);
      setSyncResult({
        success: true,
        message: `${result.synced} plan(s) synced to Stripe${result.skipped ? `, ${result.skipped} skipped` : ''}`,
      });
    } catch (error: any) {
      setSyncResult({
        success: false,
        message: error.response?.data?.error || 'Failed to sync plans',
      });
    } finally {
      setSyncing(false);
    }
  }

  const apiConnected = hasSecretKey;
  const webhookConfigured = hasWebhookSecret;

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Stripe Payments</h1>
              <p className="text-indigo-100 mt-1 text-sm">
                Configure Stripe integration for subscription billing
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                config.stripe_mode === 'live'
                  ? 'bg-green-500/20 text-green-100'
                  : 'bg-yellow-500/20 text-yellow-100'
              }`}>
                {config.stripe_mode === 'live' ? 'Live Mode' : 'Test Mode'}
              </span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Configuration */}
            <div className="lg:col-span-2 space-y-6">
              {/* API Keys */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">API Keys</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Enter your Stripe API keys from the{' '}
                  <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">
                    Stripe Dashboard
                  </a>
                </p>

                {/* Mode Toggle */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Environment</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfig(prev => ({ ...prev, stripe_mode: 'test' }))}
                      className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                        config.stripe_mode === 'test'
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 ring-1 ring-yellow-400 dark:ring-yellow-700'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      Test Mode
                    </button>
                    <button
                      onClick={() => setConfig(prev => ({ ...prev, stripe_mode: 'live' }))}
                      className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                        config.stripe_mode === 'live'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 ring-1 ring-green-400 dark:ring-green-700'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      Live Mode
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Publishable Key */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Publishable Key
                    </label>
                    <input
                      type="text"
                      value={config.stripe_publishable_key}
                      onChange={e => setConfig(prev => ({ ...prev, stripe_publishable_key: e.target.value }))}
                      placeholder={config.stripe_mode === 'test' ? 'pk_test_...' : 'pk_live_...'}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Used on the client side for Stripe.js</p>
                  </div>

                  {/* Secret Key */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Secret Key
                      {hasSecretKey && !config.stripe_secret_key && (
                        <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-normal">Saved (encrypted)</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type={showSecret ? 'text' : 'password'}
                        value={config.stripe_secret_key}
                        onChange={e => setConfig(prev => ({ ...prev, stripe_secret_key: e.target.value }))}
                        placeholder={hasSecretKey ? 'Enter new key to update...' : (config.stripe_mode === 'test' ? 'sk_test_...' : 'sk_live_...')}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <button
                        onClick={() => setShowSecret(!showSecret)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showSecret ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Used on the server side for Stripe API calls. Stored encrypted.</p>
                  </div>

                  {/* Webhook Secret */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Webhook Signing Secret
                      {hasWebhookSecret && !config.stripe_webhook_secret && (
                        <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-normal">Saved (encrypted)</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type={showWebhookSecret ? 'text' : 'password'}
                        value={config.stripe_webhook_secret}
                        onChange={e => setConfig(prev => ({ ...prev, stripe_webhook_secret: e.target.value }))}
                        placeholder={hasWebhookSecret ? 'Enter new secret to update...' : 'whsec_...'}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <button
                        onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showWebhookSecret ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Used to verify webhook events from Stripe. Stored encrypted.</p>
                  </div>
                </div>

                {/* Save */}
                <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-full hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </button>
                  <button
                    onClick={handleTestConnection}
                    disabled={testing || !hasSecretKey}
                    className="px-5 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {testing ? 'Testing...' : 'Test Connection'}
                  </button>
                  {saved && (
                    <span className="text-sm text-green-600 dark:text-green-400 font-medium">Configuration saved</span>
                  )}
                  {saveError && (
                    <span className="text-sm text-red-600 dark:text-red-400 font-medium">{saveError}</span>
                  )}
                  {testResult && (
                    <span className={`text-sm font-medium ${testResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {testResult.message}
                    </span>
                  )}
                </div>
              </div>

              {/* Webhook Configuration */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Webhook Configuration</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Configure your Stripe webhook to send events to Nivoon
                </p>

                <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Webhook Endpoint URL</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm font-mono text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-200 dark:border-gray-600 select-all">
                      https://www.nivoon.com/api/stripe/webhook
                    </code>
                    <button
                      onClick={() => navigator.clipboard.writeText('https://www.nivoon.com/api/stripe/webhook')}
                      className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Required Events</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Enable these events in your Stripe webhook settings:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[
                      'checkout.session.completed',
                      'customer.subscription.created',
                      'customer.subscription.updated',
                      'customer.subscription.deleted',
                      'invoice.payment_succeeded',
                      'invoice.payment_failed',
                    ].map(event => (
                      <div key={event} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700">
                        <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <code className="text-xs font-mono text-gray-700 dark:text-gray-300">{event}</code>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sync Pricing Plans to Stripe */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Sync Pricing Plans</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Create Stripe Products and Prices from your Nivoon pricing plans. This maps each role-based plan (Taskboard User, PM Lite, Project Manager) to Stripe so subscriptions can be billed.
                </p>

                <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">How billing works</h3>
                  <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
                    <p>Each company is billed monthly based on their users' roles:</p>
                    <div className="grid grid-cols-2 gap-1 mt-2">
                      {billingPlans.map(plan => (
                        <React.Fragment key={plan.id}>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{plan.name}</span>
                          <span>${plan.price_monthly}/user/month</span>
                        </React.Fragment>
                      ))}
                    </div>
                    {billingPlans.length === 0 && (
                      <p className="text-gray-400 italic mt-1">No billable plans configured. Add role-based pricing plans first.</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSyncPlans}
                    disabled={syncing || !hasSecretKey}
                    className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-full hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    {syncing ? 'Syncing...' : 'Sync Plans to Stripe'}
                  </button>
                  {syncResult && (
                    <span className={`text-sm font-medium ${syncResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {syncResult.message}
                    </span>
                  )}
                </div>
                {!hasSecretKey && (
                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">Save your API keys first before syncing plans.</p>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Connection Status */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-4">Connection Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">API Connection</span>
                    <span className={`flex items-center gap-1.5 text-xs font-medium ${
                      apiConnected ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${apiConnected ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}></span>
                      {apiConnected ? 'Configured' : 'Not configured'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Webhook</span>
                    <span className={`flex items-center gap-1.5 text-xs font-medium ${
                      webhookConfigured ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${webhookConfigured ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}></span>
                      {webhookConfigured ? 'Configured' : 'Not configured'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Environment</span>
                    <span className={`text-xs font-medium ${
                      config.stripe_mode === 'live'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-yellow-600 dark:text-yellow-400'
                    }`}>
                      {config.stripe_mode === 'live' ? 'Live' : 'Test'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-4">Stripe Resources</h3>
                <div className="space-y-2">
                  {[
                    { label: 'Stripe Dashboard', url: 'https://dashboard.stripe.com' },
                    { label: 'API Keys', url: 'https://dashboard.stripe.com/apikeys' },
                    { label: 'Webhook Endpoints', url: 'https://dashboard.stripe.com/webhooks' },
                    { label: 'Products & Prices', url: 'https://dashboard.stripe.com/products' },
                    { label: 'Customer Portal', url: 'https://dashboard.stripe.com/settings/billing/portal' },
                    { label: 'Stripe Documentation', url: 'https://stripe.com/docs' },
                  ].map(link => (
                    <a
                      key={link.label}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>

              {/* Setup Checklist */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-4">Setup Checklist</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Create Stripe account', done: true },
                    { label: 'Add API keys', done: !!config.stripe_publishable_key && hasSecretKey },
                    { label: 'Sync pricing plans to Stripe', done: plansSynced },
                    { label: 'Configure webhook endpoint', done: hasWebhookSecret },
                    { label: 'Create company subscriptions', done: false },
                    { label: 'Test a billing cycle', done: false },
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                        step.done
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                      }`}>
                        {step.done ? (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <span className="text-[10px] font-bold">{i + 1}</span>
                        )}
                      </div>
                      <span className={`text-sm ${step.done ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-700 dark:text-gray-300'}`}>
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
